import React, { useCallback, useEffect, useState } from "react";
import "./FarmInfo.scss";
import ProfileMap from "@components/ProfileMap/ProfileMap";
import { faLocationDot, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Row, Col, Input, Button, Select, Image } from "antd";
import TextArea from "antd/es/input/TextArea";
import { IFarmInfo, TFarmInfo } from "@utils/Interfaces";
import Gallery from "@containers/Gallery/Gallery";
import { useUser } from "contexts/UserContext";
import Validates from "@utils/Validates";
import userApi from "services/UserApi";
import Helpers from "@utils/Helpers";
import { DeleteOutlined } from "@ant-design/icons";
import { errorLog } from "logging";
import ImageCropper from "@components/ImageCropper/ImageCropper";
import { useAuth } from "contexts/AuthContext";
import { GalleryApi } from "services/GalleryApi";

interface CustomStyle extends React.CSSProperties {
  "--banner-image": string;
}

const FarmInfo: React.FC<TFarmInfo> = ({
  user,
  setUser,
  isUpdating,
  setIsUpdating,
}) => {
  const { updateUserProfile, userAddresses } = useUser();
  const { setLoading } = useAuth();
  const [editFarm, setEditFarm] = useState<IFarmInfo>({
    ...user.farm_info,
    farm_images: user.farm_info.farm_images ? user.farm_info.farm_images : [],
  });
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [uploadType, setUploadType] = useState("");
  const [isOpenCropper, setIsOpenCropper] = useState(false);
  const [avatarForCrop, setAvatarForCrop] = useState("");

  useEffect(() => {
    if (userAddresses) {
      const positionGr: any = [];
      userAddresses.map((item: any) => {
        positionGr.push([item.lat || 0, item.lon || 0]);
      });
    }
  }, [userAddresses]);

  const getUserAddress = useCallback(() => {
    if (!editFarm?.address_id) return;
    const address = userAddresses.find(
      (item: any) => item.address_id === editFarm?.address_id
    );
    return address;
  }, [editFarm?.address_id]);

  const handleInputChange = (key: string, value: any) => {
    if (key === "farm_size") {
      value = Validates.validateNumberForm(value);
    }
    setEditFarm({
      ...editFarm,
      [key]: key === "farm_size" ? parseInt(value) : value,
    });
  };

  const handleUpdate = async () => {
    let payload = { ...editFarm };
    if (!editFarm.farm_images) {
      payload.farm_images = [];
    }
    if (!Validates.validateValidWebsite(editFarm.website || "")) {
      Helpers.notification.error("Invalid website format");
      return;
    }
    setIsUpdating(true);
    try {
      const response = await userApi.updateFarmProfile(payload);
      if (response.status === 200) {
        handleUpdateSucess(
          response.data.data.access_token,
          response.data.data.refresh_token
        );
      }
    } catch (error) {
      setIsUpdating(false);
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("FarmInfo.tsx - handleUpdate", error);
    }
  };

  const handleUpdateSucess = (access: string, refresh: string) => {
    const userInfo = Helpers.decodeToken(access);
    setEditFarm(userInfo.user.farm_info);
    setUser(userInfo.user);
    setIsUpdating(false);
    updateUserProfile(access, refresh);
    Helpers.showSuccessUpdated("Update farm successfully");
  };

  const handleSelectImages = (images: any) => {
    if (images.length === 0) {
      return;
    }
    if (uploadType === "farm") {
      const newImageList = [
        ...editFarm.farm_images,
        ...images.map((item: any) => item.url),
      ];
      setEditFarm({
        ...editFarm,
        farm_images: newImageList,
      });
    } else {
      setAvatarForCrop(images[0]);
      setIsOpenCropper(true);
    }
  };

  const handleRemovePreview = (indexToRemove: number) => {
    const filteredImages: any = editFarm.farm_images.filter(
      (item, index) => indexToRemove !== index
    );
    setEditFarm({ ...editFarm, farm_images: filteredImages });
  };

  const handleCropAvatarComplete = async (croppedImg: any, documentId: any) => {
    setLoading(true);
    const response = await fetch(croppedImg);
    const blob = await response.blob();
    const file = new File([blob], `farm-crop-${documentId}.jpg`, {
      type: blob.type,
    });

    const formData = new FormData();
    formData.append("gallery", file);
    formData.append(
      "gallery_type",
      uploadType === "avatar" ? "profile" : "banner"
    );
    formData.append("gallery_is_crop", "true");
    try {
      const response = await GalleryApi.uploadMedia(formData);
      if (response.status === 200) {
        setLoading(false);
        setIsOpenCropper(false);
        if (uploadType === "banner") {
          setEditFarm({ ...editFarm, banner: response.data.data[0].url });
        } else {
          setEditFarm({ ...editFarm, avatar: response.data.data[0].url });
        }
      }
    } catch (error) {
      Helpers.notification.error("Can not upload image");
      errorLog("GalleryContent.tsx - handleUpload", error);
    }
  };

  return (
    <div className="FarmInfo">
      <div className="section head-section">
        <div
          className="head-background"
          style={
            {
              "--banner-image": `url('${editFarm.banner}')`,
            } as CustomStyle
          }
          onClick={() => {
            setUploadType("banner");
            setIsGalleryOpen(true);
          }}
        >
          <div className="banner-layer">Edit</div>
        </div>
        <div className="avatar-wrap-position">
          <div
            className="avatar-wrap"
            onClick={() => {
              setUploadType("avatar");
              setIsGalleryOpen(true);
            }}
          >
            <div className="avatar-overlay">Edit</div>
            {editFarm?.avatar || editFarm?.avatar !== "" ? (
              <Avatar
                size={100}
                src={editFarm.avatar}
                className="farm-avatar"
              />
            ) : (
              <Avatar className="farm-avatar-default">
                {editFarm.farm_name ? editFarm.farm_name[0].toUpperCase() : "F"}
              </Avatar>
            )}
          </div>
        </div>
        <div className="main-head">
          <div className="user-name">{user.farm_info.farm_name}</div>
          <div className="user-location">
            <FontAwesomeIcon icon={faLocationDot} color="#378440" />
            <span>{getUserAddress()?.street || ""}</span>
          </div>
        </div>
      </div>
      <div className="section personal-section">
        <div className="section-title">Farm Information</div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div className="form-group">
              <div className="title">
                Farm name<span className="text-danger">*</span>
              </div>
              <Input
                size="large"
                variant="filled"
                value={editFarm.farm_name || ""}
                onChange={(e) => handleInputChange("farm_name", e.target.value)}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Farm Size</div>
              <Input
                size="large"
                variant="filled"
                value={editFarm.farm_size || ""}
                onChange={(e) => handleInputChange("farm_size", e.target.value)}
                suffix={"acres"}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Website</div>
              <Input
                size="large"
                variant="filled"
                value={editFarm.website || ""}
                onChange={(e) => handleInputChange("website", e.target.value)}
              />
              {!Validates.validateValidWebsite(editFarm.website || "") && (
                <div
                  className="error-message"
                  style={{ color: "red", marginTop: 4 }}
                >
                  Invalid website format
                </div>
              )}
            </div>
          </Col>
          <Col span={24}>
            <div className="form-group">
              <div className="title">Bio</div>
              <TextArea
                size="large"
                variant="filled"
                rows={5}
                value={editFarm.farm_bio || ""}
                onChange={(e) => handleInputChange("farm_bio", e.target.value)}
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className="farm-images-group">
        <Row gutter={[16, 16]} style={{ display: "flex" }}>
          {Array.from({ length: 9 }).map((_, index) => {
            if (!editFarm.farm_images?.[index]) {
              return (
                <Col span={8} key={index}>
                  <div
                    className="upload-section"
                    onClick={() => {
                      setUploadType("farm");
                      setIsGalleryOpen(true);
                    }}
                  >
                    <div className="upload-icon">
                      <FontAwesomeIcon icon={faUpload} />
                    </div>
                    <div className="item-desc">
                      <span>Click to upload</span> or select from gallery
                    </div>
                  </div>
                </Col>
              );
            }
            return (
              <Col span={8} key={index}>
                <div className="img-preview-wrap">
                  <div className="img-preview-overlay">
                    <Button
                      variant="link"
                      color="danger"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemovePreview(index)}
                    />
                  </div>
                  <img
                    className="img-preview"
                    src={editFarm.farm_images[index]}
                    alt="preview"
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
      <Gallery
        isGalleryOpen={isGalleryOpen}
        submitGallery={handleSelectImages}
        cancelGallery={() => setIsGalleryOpen(false)}
        limit={uploadType === "farm" ? 9 : 1}
        activeKey={
          uploadType === "banner" ? "2" : uploadType === "farm" ? "3" : "1"
        }
      />
      <ImageCropper
        isOpenCropper={isOpenCropper}
        closeCropper={() => setIsOpenCropper(false)}
        selectedImg={avatarForCrop}
        onComplete={handleCropAvatarComplete}
        type={uploadType === "avatar" ? uploadType : "banner"}
      />
      <div className="form-submit">
        <Button
          className="save-btn"
          onClick={handleUpdate}
          disabled={isUpdating === true}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default FarmInfo;
